// tenant-overrides.ts wraps the HTTP proxy to the registry's tenant-config.*,
// tenant-secret.*, and tenant-limits.* methods. Plan 46 Phase 5.
//
// The admin backend is expected to expose these RPC methods under
// `/api/tenant-overrides/...` and forward them to the registry over NATS.
// organization_id is always derived server-side from the authenticated user
// and never sent in the request body.

import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';

const logPrefix = 'admin-ui:tenant-overrides';

export interface TenantConfigValue {
    key: string;
    value: string;
    modified: string;
    updatedBy?: string;
}

export interface TenantSecretRef {
    key: string;
    provider: string;
    ref: string;
    field?: string;
    modified: string;
    updatedBy?: string;
}

export interface TenantLimitsOverride {
    timeoutMs?: number | null;
    memoryMb?: number | null;
    cpuMillicores?: number | null;
    modified?: string;
    updatedBy?: string;
}

export interface ListConfigParams {
    cap: string;
}

export interface SetConfigParams {
    cap: string;
    key: string;
    value: string;
}

export interface DeleteConfigParams {
    cap: string;
    key: string;
}

export interface SetSecretParams {
    cap: string;
    key: string;
    provider: string;
    ref: string;
    field?: string;
}

export interface DeleteSecretParams {
    cap: string;
    key: string;
}

export interface SetLimitsParams {
    cap: string;
    timeoutMs?: number | null;
    memoryMb?: number | null;
    cpuMillicores?: number | null;
}

export interface DeleteLimitsParams {
    cap: string;
}

export async function listTenantConfig(params: ListConfigParams): Promise<TenantConfigValue[]> {
    try {
        const { data } = await api.get<{ values: TenantConfigValue[] }>('/tenant-overrides/config', {
            params: { cap: params.cap },
        });
        return data?.values ?? [];
    } catch (err) {
        logger.error(`${logPrefix}:listTenantConfig - failed`, { err, cap: params.cap });
        throw err;
    }
}

export async function setTenantConfig(params: SetConfigParams): Promise<void> {
    try {
        await api.post('/tenant-overrides/config', params);
    } catch (err) {
        logger.error(`${logPrefix}:setTenantConfig - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function deleteTenantConfig(params: DeleteConfigParams): Promise<void> {
    try {
        await api.delete('/tenant-overrides/config', { params });
    } catch (err) {
        logger.error(`${logPrefix}:deleteTenantConfig - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function listTenantSecrets(params: ListConfigParams): Promise<TenantSecretRef[]> {
    try {
        const { data } = await api.get<{ secrets: TenantSecretRef[] }>('/tenant-overrides/secrets', {
            params: { cap: params.cap },
        });
        return data?.secrets ?? [];
    } catch (err) {
        logger.error(`${logPrefix}:listTenantSecrets - failed`, { err, cap: params.cap });
        throw err;
    }
}

export async function setTenantSecret(params: SetSecretParams): Promise<void> {
    try {
        await api.post('/tenant-overrides/secrets', params);
    } catch (err) {
        logger.error(`${logPrefix}:setTenantSecret - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function deleteTenantSecret(params: DeleteSecretParams): Promise<void> {
    try {
        await api.delete('/tenant-overrides/secrets', { params });
    } catch (err) {
        logger.error(`${logPrefix}:deleteTenantSecret - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function getTenantLimits(params: DeleteLimitsParams): Promise<TenantLimitsOverride> {
    try {
        const { data } = await api.get<TenantLimitsOverride>('/tenant-overrides/limits', {
            params: { cap: params.cap },
        });
        return data ?? {};
    } catch (err) {
        logger.error(`${logPrefix}:getTenantLimits - failed`, { err, cap: params.cap });
        throw err;
    }
}

export async function setTenantLimits(params: SetLimitsParams): Promise<void> {
    try {
        await api.post('/tenant-overrides/limits', params);
    } catch (err) {
        logger.error(`${logPrefix}:setTenantLimits - failed`, { err, cap: params.cap });
        throw err;
    }
}

export async function deleteTenantLimits(params: DeleteLimitsParams): Promise<void> {
    try {
        await api.delete('/tenant-overrides/limits', { params });
    } catch (err) {
        logger.error(`${logPrefix}:deleteTenantLimits - failed`, { err, cap: params.cap });
        throw err;
    }
}

// ── Secret values (registry provider) ───────────────────────────────────

export interface SetSecretValueParams {
    cap: string;
    key: string;
    value: string;
}

export interface DeleteSecretValueParams {
    cap: string;
    key: string;
}

export interface ListSecretValueKeysParams {
    cap: string;
}

export async function setTenantSecretValue(params: SetSecretValueParams): Promise<void> {
    try {
        await api.post('/tenant-overrides/secret-values', params);
    } catch (err) {
        logger.error(`${logPrefix}:setTenantSecretValue - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function deleteTenantSecretValue(params: DeleteSecretValueParams): Promise<void> {
    try {
        await api.delete('/tenant-overrides/secret-values', { params });
    } catch (err) {
        logger.error(`${logPrefix}:deleteTenantSecretValue - failed`, { err, cap: params.cap, key: params.key });
        throw err;
    }
}

export async function listTenantSecretValueKeys(params: ListSecretValueKeysParams): Promise<string[]> {
    try {
        const { data } = await api.get<{ keys: string[] }>('/tenant-overrides/secret-values', {
            params: { cap: params.cap },
        });
        return data?.keys ?? [];
    } catch (err) {
        logger.error(`${logPrefix}:listTenantSecretValueKeys - failed`, { err, cap: params.cap });
        throw err;
    }
}
